import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@application/store/useStore";
import { getAccessPoint } from "@reducers/access-points/thunk/access-point-thunk";
import localStorageAccessor from "@utility/local-storage-accessor";
const sameUid = (a, b) => Boolean(a && b) && a.toLowerCase() === b.toLowerCase();
/**
 * Whether the log page belongs to a new-hardware access point (isNewHardwareVariant), which
 * decides between the new and the original node-log layout, and whether it is the AP's own node.
 *
 * Two ways in:
 *  - `/nodes/:nodeId/logs/:apId` — a node relayed by that AP; the AP is looked up by `apId`.
 *  - `/nodes/:nodeId/logs` — the AP's own node, whose uid is the AP's uid; the AP is `nodeId`.
 *
 * The AP comes from the store (the AP details page loads it) or its cached copy. Only the
 * `apId` form fetches it when missing; a bare node uid may be an ordinary node, which is not an
 * AP, so it is never requested. Whenever the AP is unknown the original layout applies.
 */
export const useIsNewHardwareAccessPoint = (apId, nodeId) => {
    const dispatch = useAppDispatch();
    const storedAccessPoint = useAppSelector((state) => state.accessPoint.selectedAccessPoint);
    const accessPointUid = apId !== null && apId !== void 0 ? apId : nodeId;
    // The cached copy (survives a refresh) is only parsed when the store does not have the AP.
    const accessPoint = sameUid(storedAccessPoint === null || storedAccessPoint === void 0 ? void 0 : storedAccessPoint.accessPointUID, accessPointUid)
        ? storedAccessPoint
        : [localStorageAccessor.getSelectedAccessPoint()].find((ap) => sameUid(ap === null || ap === void 0 ? void 0 : ap.accessPointUID, accessPointUid));
    useEffect(() => {
        if (apId && !accessPoint) {
            dispatch(getAccessPoint(apId));
        }
        // Fetch once per route AP; later store updates must not trigger another request.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, apId]);
    const isNewUi = Boolean(accessPoint === null || accessPoint === void 0 ? void 0 : accessPoint.isNewHardwareVariant);
    return { isNewUi, isApNode: isNewUi && !apId };
};
